import React from 'react';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import LinkedTasksCell from 'components/LinkedTasksCell';

import { VatixError } from 'components/Error/types';

import Grid, { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { LinkedTaskType } from 'utils/api/modules/tasks/tasks.types';
import { formatMoment } from 'utils/formatters/timezone';
import { useStore } from 'utils/hooks/store';
import routes from 'core/routes';

import { getEntityGridColumns } from 'components/Grid/components/EntitiesGridColumn/EntitiesGridColumn';

import { EntityModules, FULL_DATETIME_COLUMN_MIN_WIDTH, FULL_DATETIME_FORMAT } from 'core/constants';
import { InspectionStatusOperator } from 'components/Grid/operators/Status/Status';
import { env } from 'config';
import BasicDateOperators from 'components/Grid/operators/Date/Date';

export const formsListBasicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    nativeFilter: true,
    flex: 2,
  },
  {
    // ToDo: implement sorting on backend
    sortable: false,
    field: 'location',
    nativeFilter: true,
    headerName: 'Location',
    renderCell: defaultRenderers.location,
    valueFormatter: defaultFormatters.location,
    valueGetter: (value: unknown) => ({ value }),
    flex: 1,
  },
  {
    field: 'reporter',
    headerName: 'Auditor',
    nativeFilter: 'reporters',
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    filterOperators: defaultOperators.user(),
    valueGetter: (value: unknown) => ({ value }),
    flex: 2,
  },
  {
    maxWidth: 100,
    // ToDo: implement sorting on backend
    sortable: false,
    headerName: 'Tasks',
    field: 'linkedTasks',
    renderCell: ({ value }: { value: LinkedTaskType }) => <LinkedTasksCell linkedTasks={value} />,
    flex: 1,
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    nativeFilter: 'submitted',
    renderCell: ({ value }: { value: string }) => value,
    valueGetter: (_value: unknown, row: { submitted: null | string }) => (row.submitted ? 'Submitted' : 'In progress'),
    filterOperators: InspectionStatusOperator(),
    flex: 1,
  },
  {
    field: 'submitted',
    headerName: 'Submitted',
    nativeFilter: 'submitted',
    filterOperators: defaultOperators.date(),
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string | null }) => (
      <span data-testid="forms-submitted-at">
        {value !== null ? formatMoment(value, FULL_DATETIME_FORMAT) : 'Not submitted'}
      </span>
    ),
    valueFormatter: (value: string | null) => formatMoment(value, FULL_DATETIME_FORMAT),
    flex: 1,
  },
  {
    sortable: false,
    field: 'created',
    headerName: 'Created',
    nativeFilter: 'created',
    defaultVisibility: false,
    filterOperators: BasicDateOperators(),
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string | null }) => (
      <span data-testid="forms-created-at">{value !== null ? formatMoment(value, FULL_DATETIME_FORMAT) : 'N/A'}</span>
    ),
    valueFormatter: (value: string | null) => formatMoment(value, FULL_DATETIME_FORMAT),
    flex: 1,
  },
] as unknown as ColumnDefinition[];

const FormsList: React.FunctionComponent<{ onError: (error: VatixError) => void }> = ({
  onError,
}): React.ReactElement => {
  const {
    routing,
    session: { user },
  } = useStore();
  const onRowClick: GridEventListener<'rowClick'> = (form: GridRowParams): void => {
    routing.push(reverse(routes.dashboard.audits.forms.details, { formId: form.row.uuid }));
  };

  // get only sites contacts and assets
  const entityModules = user?.activeEntityModules
    ? user.activeEntityModules.filter((module) =>
        [EntityModules.Sites, EntityModules.Contacts, EntityModules.Assets, EntityModules.Documents].includes(module)
      )
    : [];

  return (
    <Grid
      entity="forms"
      onError={onError}
      onRowClick={onRowClick}
      sortBy={{ field: 'submitted', sort: 'desc' }}
      customNoRowsText="No forms have been created yet"
      dataURL={`${env.REACT_APP_WORKFLOWS_API_URL}inspections/`}
      basicColumns={[...formsListBasicColumns, ...getEntityGridColumns(entityModules)]}
    />
  );
};

export default FormsList;
